import React, { useState } from 'react';
import LoadingScreen from '../../../../../Reutilizables/FetchWithGif';

/**
 * Modal que muestra todos los comprobantes de pago de una cuota con pagos parciales
 * Permite ver múltiples comprobantes cuando se han realizado varios pagos parciales
 */
const ComprobantesParcialesModal = ({
  show,
  onClose,
  cuota,
  prestamo,
  pagos = [],
  loading = false
}) => {
  const [comprobanteSeleccionado, setComprobanteSeleccionado] = useState(null);

  
  React.useEffect(() => {
    if (show) {
      setComprobanteSeleccionado(null);
    }
  }, [show, cuota?.idCuota]);

  if (!show) return null;

  // Validación de datos antes de procesar
  const pagosValidos = Array.isArray(pagos) ? pagos : [];
  const pagosOrdenados = pagosValidos.length > 0 
    ? [...pagosValidos].sort((a, b) => new Date(b.fecha_pago) - new Date(a.fecha_pago))
    : [];

  const formatMonto = (monto) => {
    const num = parseFloat(monto) || 0;
    return `S/ ${num.toFixed(2)}`;
  };

  const handleVerComprobante = (pago) => {
    setComprobanteSeleccionado(pago);
  };

  const handleCerrarVisor = () => {
    setComprobanteSeleccionado(null);
  };

  return (
    <>
      {loading && <LoadingScreen />}
      <div className="modal-overlay">
        <div className="modal-panel modal-panel-xl relative p-6">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 text-slate-500 transition hover:text-slate-700"
          title="Cerrar"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="mb-6">
          <h3 className="modal-title mb-2">
            Comprobantes de Pago - Cuota N°{cuota?.numero_cuota}
          </h3>
          <div className="modal-text">
            <p>Préstamo #{prestamo?.idPrestamo} - {prestamo?.modalidad}</p>
            <p>Monto cuota: {formatMonto(cuota?.monto)}</p>
            <p className="font-semibold text-slate-900">
              Total de pagos registrados: {pagosOrdenados.length}
            </p>
          </div>
        </div>

        {pagosOrdenados.length === 0 ? (
          <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4">
            <p className="text-yellow-800 font-medium">No hay pagos registrados para esta cuota.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lista de pagos */}
            <div className="space-y-3">
              <h4 className="text-lg font-semibold text-gray-800 mb-3">
                Historial de Pagos ({pagosOrdenados.length})
              </h4>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
                {pagosOrdenados.map((pago, index) => {
                  const montoPagado = 
                    parseFloat(pago.pago_aplicado_cuota || 0) + 
                    parseFloat(pago.pago_aplicado_mora || 0);
                  const esUltimoPago = index === 0;

                  return (
                    <div
                      key={pago.idPago || index}
                      className={`rounded-lg border p-4 transition-shadow hover:shadow-md ${
                        esUltimoPago 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          {esUltimoPago && (
                            <span className="inline-block bg-green-600 text-white text-xs px-2 py-1 rounded-full mb-2">
                              Pago más reciente
                            </span>
                          )}
                          <p className="text-sm font-semibold text-gray-800">
                            Pago N°{pagosOrdenados.length - index}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-black">
                            {formatMonto(montoPagado)}
                          </p>
                        </div>
                      </div>

                      <div className="text-xs text-gray-600 space-y-1 mb-3">
                        <p>• A cuota: {formatMonto(pago.pago_aplicado_cuota || 0)}</p>
                        <p>• A mora: {formatMonto(pago.pago_aplicado_mora || 0)}</p>
                        {pago.excedente > 0 && (
                          <p className="text-blue-600 font-semibold">
                            • Excedente: {formatMonto(pago.excedente)}
                          </p>
                        )}
                        {pago.numero_operacion && (
                          <p>• N° Operación: {pago.numero_operacion}</p>
                        )}
                      </div>

                      <button
                        onClick={() => handleVerComprobante(pago)}
                        className="w-full rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
                      >
                        Ver Comprobante
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Visor de comprobante */}
            <div className="border-l border-slate-200 pl-6">
              {comprobanteSeleccionado ? (
                <div className="sticky top-0">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-lg font-semibold text-gray-800">
                      Comprobante de Pago
                    </h4>
                    <button
                      onClick={handleCerrarVisor}
                      className="text-slate-500 transition hover:text-slate-700"
                      title="Cerrar visor"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {comprobanteSeleccionado.comprobante_url ? (
                    <iframe
                      src={comprobanteSeleccionado.comprobante_url}
                      title={`Comprobante ${comprobanteSeleccionado.idPago}`}
                      className="h-[65vh] w-full rounded-lg border border-slate-300"
                    />
                  ) : (
                    <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4">
                      <p className="text-yellow-800 font-medium">
                        No hay comprobante disponible para este pago.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex h-full items-center justify-center text-slate-400">
                  <div className="text-center">
                    <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-lg font-medium">Selecciona un pago</p>
                    <p className="text-sm">para ver su comprobante</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="modal-actions mt-6 border-t border-slate-200 pt-4">
          <button
            onClick={onClose}
            className="btn-modal-neutral"
          >
            Cerrar
          </button>
        </div>
      </div>
      </div>
    </>
  );
};

export default ComprobantesParcialesModal;
